import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Inbox, RefreshCcw, Search, Mail, CheckCircle2, Circle } from "lucide-react";

/**
 * ContactMessagesAdmin — browse everything that arrives via /api/contact.
 * Read-only screen (mark read/unread only). All destructive follow-ups
 * happen by replying from your inbox — the persisted row is the audit trail.
 */
const TOPICS = [
    { key: "", label: "All topics" },
    { key: "general", label: "General" },
    { key: "sales", label: "Sales" },
    { key: "support", label: "Support" },
    { key: "privacy", label: "Privacy" },
    { key: "security", label: "Security" },
    { key: "press", label: "Press" },
];

export default function ContactMessagesAdmin() {
    const [state, setState] = useState({ loading: true, rows: [], summary: {} });
    const [topic, setTopic] = useState("");
    const [q, setQ] = useState("");
    const [unreadOnly, setUnreadOnly] = useState(false);
    const [selected, setSelected] = useState(null);

    const load = async () => {
        setState((s) => ({ ...s, loading: true }));
        const params = { limit: 100 };
        if (topic) params.topic = topic;
        if (q) params.q = q;
        if (unreadOnly) params.unread_only = true;
        try {
            const { data } = await api.get("/admin/contact-messages", { params });
            setState({ loading: false, rows: data.rows || [], summary: data.summary || {} });
        } catch {
            setState({ loading: false, rows: [], summary: {} });
        }
    };

    useEffect(() => { load(); }, [topic, unreadOnly]);

    const toggleRead = async (row) => {
        const url = `/admin/contact-messages/${row.id}/${row.read ? "unread" : "read"}`;
        try {
            await api.post(url);
            await load();
        } catch { /* noop */ }
    };

    const filteredCount = state.rows.length;
    const fmt = (v) => (v ? String(v).slice(0, 16).replace("T", " ") : "—");
    const grouped = useMemo(() => state.rows, [state.rows]);

    return (
        <div className="max-w-6xl mx-auto p-6" data-testid="admin-contact-inbox">
            <Link to="/dashboard" className="text-xs text-stone-500 hover:text-stone-900 inline-flex items-center gap-1 mb-3">
                <ArrowLeft size={12} /> Back to dashboard
            </Link>
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="text-xs font-semibold tracking-wider text-stone-500 uppercase mb-1 flex items-center gap-1.5">
                        <Inbox size={12} /> Contact inbox
                    </div>
                    <h1 className="font-display text-4xl text-stone-900 leading-none mt-2">Messages from the site.</h1>
                    <p className="text-sm text-stone-500 mt-2">
                        {state.summary.total ?? 0} total · <b className="text-amber-700">{state.summary.unread ?? 0} unread</b> · showing {filteredCount}
                    </p>
                </div>
                <button
                    onClick={load}
                    data-testid="inbox-refresh"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 px-3 py-2 text-xs font-semibold text-stone-700 hover:border-stone-400"
                >
                    <RefreshCcw size={11} /> Refresh
                </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
                {TOPICS.map((tp) => (
                    <button
                        key={tp.key || "all"}
                        onClick={() => setTopic(tp.key)}
                        data-testid={`inbox-topic-${tp.key || "all"}`}
                        className={`px-3 py-1.5 text-xs rounded-full border transition ${
                            topic === tp.key
                                ? "bg-stone-900 text-white border-stone-900"
                                : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"
                        }`}
                    >
                        {tp.label}
                    </button>
                ))}
                <label className="ml-auto inline-flex items-center gap-2 text-xs text-stone-600">
                    <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} data-testid="inbox-unread-only" />
                    Unread only
                </label>
                <div className="relative">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && load()}
                        placeholder="Search name, email, subject, message…"
                        data-testid="inbox-search"
                        className="pl-8 pr-3 py-1.5 text-xs rounded-lg border border-stone-200 w-72"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-1 border border-stone-200 rounded-xl overflow-hidden bg-white" data-testid="inbox-list">
                    {state.loading && <div className="p-6 text-sm text-stone-400">Loading…</div>}
                    {!state.loading && grouped.length === 0 && (
                        <div className="p-6 text-sm text-stone-400">Nothing here yet.</div>
                    )}
                    {grouped.map((r) => (
                        <button
                            key={r.id}
                            onClick={() => setSelected(r)}
                            data-testid={`inbox-row-${r.id}`}
                            className={`w-full text-left p-3 border-b border-stone-100 hover:bg-stone-50 transition ${
                                selected?.id === r.id ? "bg-amber-50/60" : ""
                            } ${!r.read ? "font-semibold" : ""}`}
                        >
                            <div className="flex items-start gap-2">
                                {r.read
                                    ? <CheckCircle2 size={13} className="mt-1 text-emerald-500 shrink-0" />
                                    : <Circle size={13} className="mt-1 text-amber-600 shrink-0" />}
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-stone-900 truncate">{r.name} <span className="text-stone-400 font-normal">· {r.topic}</span></div>
                                    <div className="text-xs text-stone-500 truncate">{r.subject}</div>
                                    <div className="text-[10px] text-stone-400 mt-1">{fmt(r.created_at)} UTC</div>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>

                <div className="md:col-span-2 border border-stone-200 rounded-xl bg-white p-5 min-h-[300px]" data-testid="inbox-detail">
                    {!selected ? (
                        <div className="h-full flex items-center justify-center text-sm text-stone-400">Pick a message on the left to read it.</div>
                    ) : (
                        <>
                            <div className="flex items-start justify-between gap-3 mb-3">
                                <div>
                                    <div className="text-xs font-semibold tracking-wider text-stone-500 uppercase mb-1">{selected.topic}</div>
                                    <h2 className="text-xl font-semibold text-stone-900">{selected.subject}</h2>
                                    <div className="text-xs text-stone-500 mt-1">
                                        From <b>{selected.name}</b> · <a href={`mailto:${selected.email}`} className="text-amber-700">{selected.email}</a> · {fmt(selected.created_at)} UTC
                                    </div>
                                </div>
                                <div className="flex flex-col gap-2 shrink-0">
                                    <a
                                        href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject)}`}
                                        data-testid="inbox-reply-btn"
                                        className="inline-flex items-center gap-1.5 rounded-lg bg-stone-900 px-3 py-2 text-xs font-semibold text-white hover:bg-stone-800"
                                    >
                                        <Mail size={11} /> Reply
                                    </a>
                                    <button
                                        onClick={() => toggleRead(selected)}
                                        data-testid="inbox-toggle-read"
                                        className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 px-3 py-2 text-xs font-semibold text-stone-700 hover:border-stone-400"
                                    >
                                        {selected.read ? "Mark unread" : "Mark read"}
                                    </button>
                                </div>
                            </div>
                            <div className="text-sm text-stone-800 whitespace-pre-wrap leading-relaxed border-t border-stone-100 pt-4">
                                {selected.message}
                            </div>
                            <div className="mt-6 pt-4 border-t border-stone-100 text-[11px] text-stone-500 grid grid-cols-2 gap-2">
                                <div>IP: <span className="font-mono">{selected.ip || "—"}</span></div>
                                <div>Referrer: <span className="font-mono truncate">{selected.referrer || "—"}</span></div>
                                <div className="col-span-2 truncate">UA: <span className="font-mono">{selected.user_agent || "—"}</span></div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
